import jwt_decode from "jwt-decode";
import { createStore, applyMiddleware } from "redux";
import createSagaMiddleware from "redux-saga";
import rootReducer from "./reducers";
import rootSaga from "./sagas";
import { ATTRIBUTION_STORAGE_KEY, buildPathFromSafeQuery } from "./utils/attribution";
import { getUnauthenticatedRedirectPath } from "./utils/authRedirect";
import { clearLocalStoragePreservingKeys } from "./utils/localStorage";
import { PAID_SIGNUP_TRACKED_STORAGE_KEY } from "./utils/tracking";
import { captureReferralCode } from "./utils/referral";
import url from "./constants/urlList";
const sagaMiddleware = createSagaMiddleware();

// The trusted QLearn API origin for the unauthenticated Apple exchange: derived from the exact
// URL the API layer emits (${domain}/qlearn/auth/apple), so the exemption follows the configured
// prod/dev/preprod host and nothing else.
const APPLE_EXCHANGE_PATHNAME = "/qlearn/auth/apple";
const trustedApiOrigin = (() => {
    try { return new URL(url.appleAuth, window.location.origin).origin; } catch (e) { return null; }
})();

const store = createStore(rootReducer, applyMiddleware(sagaMiddleware));

// then run the saga
sagaMiddleware.run(rootSaga);

export default store;
let decodedToken, token, isExpired = false;

export const buildQlinkEntryRedirectUrl = ({ origin, pathname, search }) => (
    `${origin}${buildPathFromSafeQuery(pathname, search)}`
);

// Referral capture now lives in utils/referral (it stores a companion
// capture timestamp so reads can expire a stale code). Kept as a thin
// re-export here so the module-load + fetch-interceptor call sites and
// store.test.js are unchanged.
export const captureReferralCodeFromSearch = (
    search = window.location.search,
    storage = window.localStorage
) => captureReferralCode(search, storage);

captureReferralCodeFromSearch();

// Interceptor
(function () {
    const originalFetch = window.fetch;
    window.fetch = function (url, options) {
        let mainArguments = arguments;
        // Skip auth header for login/signup endpoints, but allow it for profile/complete.
        // /qlearn/auth/apple is an unauthenticated session exchange (native Sign in with
        // Apple), like signin/signup/phone — it must not receive a "Bearer null" or stale
        // token. Match it by EXACT normalized pathname (not a whole-URL substring): a
        // substring match would also strip auth from unrelated protected URLs such as
        // /qlearn/user/profile?return=/qlearn/auth/apple and lookalikes like
        // /qlearn/auth/apple-admin.
        const isAppleAuthExchange = (requestUrl) => {
            try {
                const u = new URL(requestUrl, window.location.origin);
                if (u.pathname !== APPLE_EXCHANGE_PATHNAME) return false;
                // Exact pathname AND a trusted origin: same-origin (the intended relative form)
                // or the configured QLearn API origin. An alternate-origin exact-path URL
                // (e.g. https://alternate.test/qlearn/auth/apple) retains normal Authorization.
                return u.origin === window.location.origin
                    || (trustedApiOrigin !== null && u.origin === trustedApiOrigin);
            } catch (e) {
                return false;
            }
        };
        const skipAuthHeader = (url.match("/auth/signin") || url.match("/auth/signup") ||
                               isAppleAuthExchange(url) ||
                               url.match("/auth/phone/send-otp") || url.match("/auth/phone/verify-otp") ||
                               url.match("/diagnostic/absite/program-codes/request") ||
                               url.match("/js/"));
        if (!skipAuthHeader) {
            if (!options) {
                options = {
                    headers: {},
                };
                mainArguments = Array.prototype.slice.call(arguments);
                mainArguments.push(options);
            }

            token = window.localStorage.getItem("token");

            if (token) {
                decodedToken = jwt_decode(token);
                isExpired = new Date().getTime() >= decodedToken.exp * 1000;
            }

            captureReferralCodeFromSearch();

            const isQlinkEntry = window.location.pathname.includes("/question/view") ||
                window.location.pathname.includes("/qlink/preview");

            if (isQlinkEntry) {
                const urlParams = new URLSearchParams(window.location.search);
                const userAcqToken = urlParams.get('token');

                if (userAcqToken) {
                    window.localStorage.setItem("userAcquisitiontoken", userAcqToken);

                    const redirectUrl = buildQlinkEntryRedirectUrl({
                        origin: window.location.origin,
                        pathname: window.location.pathname,
                        search: window.location.search
                    });
                    window.location.href = redirectUrl;
                }
            }

            const currentPath = window.location.pathname;
            const isAuthPage = currentPath.includes("/login") ||
                currentPath.includes("/register") ||
                currentPath.includes("/forget-password") ||
                currentPath.includes("/reset-password") ||
                currentPath.includes("/verify-user") ||
                currentPath.includes("/socialAuth") ||
                currentPath.includes("/privacy-policy") ||
                currentPath.includes("/terms") ||
                currentPath.includes("/support") ||
                currentPath.includes("/absite-program-code-request") ||
                currentPath.includes("/daily-absite-question");

            if (!isAuthPage && !isQlinkEntry && (!token || isExpired)) {
                clearLocalStoragePreservingKeys(window.localStorage, [
                    ATTRIBUTION_STORAGE_KEY,
                    PAID_SIGNUP_TRACKED_STORAGE_KEY
                ]);
                // Develop's notes-to-quiz return-path behavior re-grafted through
                // validators (F2-EVENT-A-RULING axis 2): getUnauthenticatedRedirectPath
                // only ever emits /login?next=/notes-to-quiz/<token> (anchored regex)
                // or "/", and login.jsx re-validates the next param on consumption.
                window.location = getUnauthenticatedRedirectPath(window.location.pathname);
                return;
            }

            options.headers["Authorization"] = "Bearer " + token;
        }

        return originalFetch.apply(this, mainArguments);
    };
})();
